import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FocusableParent } from '../navigation';
import SpatialNavigationWrapper from '../navigation/SpatialNavigationWrapper';
import DeleteIcon from '../Icons/delete';
import searchLayouts from './languages';
import Key from './Key';
import Platform from '../../platform';
import { Row, StyledSearchQuery } from './StyledComponents';

interface IKeyboard {
  query: string;
  maxQuerySize?: number;
  config?: {
    [key: string]: unknown;
  };
  onQueryUpdated?: (query: string) => void;
  onLeave?: () => void;
}

const Keyboard: React.FC<IKeyboard> = ({
  query: propQuery,
  maxQuerySize,
  config = {},
  onQueryUpdated,
  onLeave,
}) => {
  const [query, setQuery] = useState(propQuery);
  const [selectedLanguage, setSelectedLanguage] = useState('en');

  const defaultFocusedElement = useRef();

  const focusDefaultCharacter = () => {
    defaultFocusedElement && SpatialNavigationWrapper.focus(defaultFocusedElement.current);
  };

  useEffect(() => {
    focusDefaultCharacter();
    document.addEventListener('keydown', onKeydown, true);

    return () => {
      document.removeEventListener('keydown', onKeydown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKeydown = ({ keyCode }) => {
    const numberValue = Platform.getKeyCodeNumericValue(keyCode);
    if (numberValue) insertCharacter(numberValue);
  };

  const updateQuery = useCallback(
    (query: string) => {
      setQuery(query);

      onQueryUpdated && onQueryUpdated(query);
    },
    [onQueryUpdated],
  );

  const insertCharacter = useCallback(
    (character: string) => {
      if (maxQuerySize && query.length >= maxQuerySize) return;

      updateQuery(query + character);
    },
    [maxQuerySize, query, updateQuery],
  );

  const removeCharacter = useCallback(() => {
    updateQuery(query.slice(0, -1));
  }, [updateQuery, query]);

  const runSpecial = useCallback(
    (special: string, keyboard?: string) => {
      switch (special) {
        case 'space':
          insertCharacter(' ');
          break;
        case 'symbols':
          setSelectedLanguage(keyboard);
          break;
        case 'clear':
          updateQuery('');
          break;
        case 'delete':
          removeCharacter();
          break;
        default:
          break;
      }
    },
    [insertCharacter, updateQuery, removeCharacter],
  );

  const onClick = useCallback(
    ({ character, special, keyboard }) => {
      if (special) {
        return runSpecial(special, keyboard);
      }

      if (character) insertCharacter(character);
    },
    [runSpecial, insertCharacter],
  );

  const renderRow = (row, index) => {
    return (
      <Row key={`keyboard-row-${index}`}>
        {row.keys.map((key, index) => {
          const content = getKeyContent(key);

          return (
            <Key
              keyMeta={key}
              content={content}
              key={`key-${key.character || ''}-${index}`}
              ref={key.focused && defaultFocusedElement}
              onEnter={onClick}
            />
          );
        })}
      </Row>
    );
  };

  const getKeyContent = (key) => {
    if (key.icon) return getIcon(key.icon);
    return key.character;
  };

  const getIcon = (icon) => {
    if (icon === 'delete') return <DeleteIcon />;
  };

  const getLayout = () => searchLayouts[selectedLanguage];

  const renderLayout = () => {
    const layout = getLayout();

    return layout.map((row, index) => renderRow(row, index));
  };

  return (
    <>
      <StyledSearchQuery isQuery={Boolean(query)}>
        {query || 'Type name of a movie, tv show or channel you need'}
      </StyledSearchQuery>
      <FocusableParent
        key={`keyboard-${selectedLanguage}`}
        sectionName={'KEYBOARD'}
        defaultSection
        config={config}
        onLeave={onLeave}
      >
        {renderLayout()}
      </FocusableParent>
    </>
  );
};

export default Keyboard;
